<template>
  <!-- 顶部导航栏：与页脚一致的设计系统（品牌色 #0056d2） -->
  <el-header
    :class="[
      'top-nav',
      {
        'top-nav--gradient': isGradientNav,
        'top-nav--periodical': isPeriodicalDetailPage
      }
    ]"
    height="60"
  >
    <div :class="['top-nav__inner', {'top-nav__inner--content': isContentPage}]">
      <!-- 品牌 -->
      <router-link to="/" class="brand">
        <img
          class="brand__logo-img"
          :src="brandLogoImg"
          :alt="brandName"
        />
      </router-link>

      <!-- 导航：数据来自后端菜单配置（Vuex permission/menuTree），配置不可用时回退本地字典 -->
      <!-- 支持二级菜单（el-submenu），后端返回的 children 数组自动渲染为下拉子菜单 -->
      <!-- 注意：分组项的 index 也用 path —— Element UI 只用 index 映射 el-menu-item 的高亮，
           分组本身永远无法被高亮，activeIndex 会把分组投影到其首个叶子（见 computed.activeIndex） -->
      <el-menu
        :default-active="activeIndex"
        mode="horizontal"
        class="nav-menu"
        :text-color="navTextColor"
        :active-text-color="navActiveTextColor"
        @select="handleSelect"
      >
        <template v-for="item in navList">
          <!-- 有子菜单 → el-submenu 下拉 -->
          <!-- key 必须以 path 为主体（语言切换时不能变），前缀只是让 v-if/v-else 两个分支的 key 互不相同 -->
          <el-submenu
            v-if="item.children && item.children.length"
            :index="item.path"
            :key="`group-${item.path}`"
          >
            <template slot="title">{{ item.label }}</template>
            <el-menu-item v-for="child in item.children" :key="`leaf-${child.path}`" :index="child.path">
              {{ child.label }}
            </el-menu-item>
          </el-submenu>
          <!-- 无子菜单 → 普通菜单项（disabled 项点击被原生禁用，不再触发 select） -->
          <el-menu-item v-else :index="item.path" class="nav-menu__item" :key="`leaf-${item.path}`" :disabled="item.disabled">
            {{ item.label }}
          </el-menu-item>
        </template>
      </el-menu>

      <!-- 弹性间隔，把头部操作推到最右侧 -->
      <span class="spacer"></span>

      <!-- 头部操作 -->
      <div class="actions">
        <el-dropdown trigger="click" @command="onLang">
          <span class="action lang"><el-image :src="'/home/eastview/common/icon_cn.svg'" style="width: 24px;height:24px;"/>{{ lang }} </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="CN">中文CN</el-dropdown-item>
            <el-dropdown-item command="EN">English EN</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <!-- 登录按钮 / Log in -->
        <el-button class="login-btn" v-show="showLoginBtn" @click="onLogin">{{ menuLocaleLabel('login') }}</el-button>
        <template v-if="!showLoginBtn">
         <div v-if="!hasAccountCenter" class="nav-btn">
           <span class="user-name" :title="currentUserName">{{ currentUserName }}</span>
          <el-button class="logout-btn" @click="onLogout">
            <i class="el-icon-switch-button"></i>
            {{ menuLocaleLabel('logout') }}
          </el-button>
         </div>
        <el-dropdown trigger="hover" v-else>
            <span class="user-name" :title="currentUserName" @click.stop="handleCommand">{{ currentUserName }}
              <i class="el-icon-arrow-down el-icon--right" v-show="hasAccountCenter"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="handleCommand" v-show="hasAccountCenter.isShowAccountCenter">{{ menuLocaleLabel('accountCenter') }}</el-dropdown-item>
              <el-dropdown-item @click.native="onLogout">{{ menuLocaleLabel('logout') }}</el-dropdown-item>
            </el-dropdown-menu>
        </el-dropdown>
        </template>
      </div>
    </div>
  </el-header>
</template>

<script>
import { mapGetters } from 'vuex'
import menuLocale from '@/i18n/modules/menu.i18n'
import { getBranding, getThemeKey } from '@/config/branding'
import {
  NAV_FALLBACK_EASTVIEW,
  NAV_FALLBACK_QIUSHI,
  buildNavFromFallback,
  buildNavFromMenuTree,
  isBackendNavUsable,
  resolveActiveIndex
} from '@/utils/navConfig'

/**
 * 顶部导航：数据来自后端菜单配置，组件只负责渲染与高亮。
 *
 * - 数据源：Vuex `permission/menuTree`（路由守卫在进入页面前已拉取），
 *   配置不可用时回退 `NAV_FALLBACK_*`（见 utils/navConfig.js）。
 * - 文案：后端 `meta.i18nKey` → menu.i18n.js；缺省时按 navConfig 的回退链解析。
 */
export default {
  name: 'TopNavBar',
  data() {
    return {
      showLoginBtn: false,
      currentUserName: ''
    }
  },
  computed: {
    ...mapGetters('permission', ['menuTree']),
    /** 当前语言：底层 i18n 已用同一个 APP_LANGUAGE 键做过持久化，这里只做映射 */
    lang() {
      return this.$language.language === 'en' ? 'EN' : '中文'
    },
    /** 当前语言的文案字典 */
    labelMap() {
      return menuLocale[this.lang === 'EN' ? 'en' : 'zh'] || menuLocale.zh
    },
    /** 后端菜单树构建出的导航（后端未配置时为 []） */
    backendNavList() {
      return buildNavFromMenuTree(this.menuTree, this.keyToLabel)
    },
    /** 客户端已注册路由路径集合，用于判断后端导航是否真的可用 */
    routePaths() {
      const routes =
        this.$router && typeof this.$router.getRoutes === 'function'
          ? this.$router.getRoutes()
          : (this.$router && this.$router.options && this.$router.options.routes) || []
      return new Set((routes || []).map((route) => route && route.path).filter(Boolean))
    },
    /**
     * 最终渲染的导航项。
     * 后端返回非空且「叶子路径能命中客户端路由」的比例达标才采用，否则整体回退本地字典，
     * 避免后端配错（如仍是报纸产品的菜单树）时把 eastview 导航换成一批死链。
     */
    navList() {
      const backend = this.backendNavList
      if (backend.length && isBackendNavUsable(backend, this.routePaths)) {
        return backend
      }
      return buildNavFromFallback(
        this.isQiushiTheme ? NAV_FALLBACK_QIUSHI : NAV_FALLBACK_EASTVIEW,
        this.keyToLabel
      )
    },
    /** 是否为 qiushi 主题（非 qiushi 不启用渐变导航与换底 logo） */
    isQiushiTheme() {
      return getThemeKey() === 'qiushi'
    },
    isEastView () {
      return getThemeKey() === 'eastview'
    },
    /** 是否有个人中心 */
    hasAccountCenter() {
      return getBranding().hasAccountCenter
    },
    accountInfo() {
      return getBranding().accountInfo
    },
    /** 是否停留在首页（qiushi 首页导航透明沉浸，非首页才用渐变背景） */
    isHomePage() {
      return this.$route.path === '/home'
    },
    isContentPage () {
      return this.$route.path.startsWith('/book/content')
    },

    /**
     * 期刊产品详情页。
     * 该页面顶部内容区使用浅蓝色背景，导航栏同步使用相同底色，
     * 让导航与期刊信息区形成一整块连续背景。
     */
    isPeriodicalDetailPage() {
      return this.$route.path === '/journal/productDetail'
    },
    /**
     * 品牌图片路由：
     * - qiushi 首页透明导航 → 深色底 logo.png
     * - qiushi 非首页渐变导航 → 白字透明底 logo_transparent.png
     * - 其余 eastview 系主题为空（走 CSS 拼图）
     */
    brandLogoImg() {
      if(this.isQiushiTheme) {
        return this.isHomePage
          ? '/home/qiushi/common/logo.png'
          : '/home/qiushi/common/logo_transparent.png'
      }
      if (this.isEastView) {
        return '/home/eastview/common/home_logo.png'
      }
      return ''
    },
    /** 是否展示整幅品牌图（qiushi 需求：不手动拼 logo） */
    isQiushiBrand() {
      return this.isQiushiTheme
    },
    /** qiushi 非首页：导航栏渐变蓝底（#16A1FE → #0379FF） */
    isGradientNav() {
      return this.isQiushiTheme && !this.isHomePage
    },
    /** 渐变导航：主菜单文字白色；透明（首页）导航沿用黑色 */
    navTextColor() {
      return this.isGradientNav ? '#fff' : '#000'
    },
    /** 激活文字色：qiushi 主题色统一为 #0379FF（含首页透明导航）；其余主题沿用 #0056d2 */
    navActiveTextColor() {
      return this.isQiushiTheme ? '#0379FF' : '#0056d2'
    },
    /** 品牌文字：qiushi 用主题 sysName 作为图片 alt；非 qiushi 沿用原「图标 + Green Apple」文案 */
    brandName() {
      return getThemeKey() === 'qiushi'
        ? (getBranding().sysName || this.menuLocaleLabel('brandName'))
        : this.menuLocaleLabel('brandName')
    },
    /**
     * 当前高亮的导航项 index（使用 path 作为唯一标识）。
     * 具体解析规则见 utils/navConfig.js 的 resolveActiveIndex —— 纯函数，便于单测。
     */
    activeIndex() {
      return resolveActiveIndex(
        this.navList,
        this.$route.path,
        this.$route.meta && this.$route.meta.activeMenu
      )
    }
  },
  created() {
    this.showLoginBtn = !this.$ls.get('ACCESS_TOKEN')
    this.currentUserName = this.readCurrentUserName()
  },
  methods: {
    /**
     * i18n key → 当前语言文案。取不到时返回 ''，由调用方继续走各自的中文兜底。
     * @param {string} key menu.i18n.js 中的键
     */
    keyToLabel(key) {
      if (!key) return ''
      return this.labelMap[key] || menuLocale.zh[key] || ''
    },
    /**
     * 从 localStorage 的 USER_INFO 读取当前用户名，用于右上角展示。
     * 兼容对象与 JSON 字符串两种写入形态，解析失败时回退到「已登录」。
     */
    readCurrentUserName() {
      try {
        const userInfo = this.$ls.get('USER_INFO')
        if (userInfo && typeof userInfo === 'object') {
          return userInfo.realname || userInfo.username || '已登录'
        }
        if (typeof userInfo === 'string' && userInfo.trim()) {
          const parsed = JSON.parse(userInfo)
          return parsed.realname || parsed.username || '已登录'
        }
      } catch (e) {}
      return '已登录'
    },
    // 通用菜单文案转换：登录、品牌等非导航菜单文字也统一从菜单字典读取。
    menuLocaleLabel(key) {
      return this.keyToLabel(key) || key
    },
    /**
     * 菜单选中回调。
     * @param {string} index - 被选中项绑定的 index（此处为 path）
     */
    handleSelect(index) {
      // 防御分支：水平菜单默认 menu-trigger="hover"，Element UI 的 submenu 只 emit
      // open/close、从不 emit select，因此分组项实际不会走到这里。保留只是为了让
      // 「index 一律等于 path」这一约定自洽，不依赖它做跳转。
      const parent = this.navList.find((n) => n.path === index && n.children && n.children.length)
      if (parent && parent.children.length) {
        if (index !== this.$route.path) {
          this.$router.push(parent.children[0].path)
        }
        return
      }
      // 普通菜单项或子菜单项 → 直接跳转
      if (index !== this.$route.path) {
        this.$router.push(index)
      }
    },
    // 语言切换：navList 是依赖 $language 的计算属性，这里只需更新底层语言。
    onLang(cmd) {
      this.$language.setLanguage(cmd.toLowerCase())
      this.$emit('lang-change', cmd)
    },
    onLogin() {
      if (this.$route.path !== '/login') {
        this.$router.push('/login')
      }
    },
    onLogout() {
      this.$store.dispatch('user/exitLogin')
    },
    handleCommand () {
      if(this.hasAccountCenter) {
        this.$router.push('/accountCenter')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.top-nav {
  /* 沉浸式导航栏：背景透明，让下方子页面背景图上提铺满导航栏区域；
     border-bottom 移除，避免在背景图上画出分隔线；
     position + z-index 保证导航栏整层（含点击命中区）恒绘制在页面背景之上。 */
  background: transparent;
  position: relative;
  z-index: 10;
  padding: 0;
  transition: background 0.2s;
}

.top-nav__inner--content {
  padding: 0 40px !important;
}

/*
 * EastView 期刊详情页：
 * 与 PeriodicalProductDetail 顶部 #eef7ff 背景保持一致，
 * 避免透明导航透出 Layout 默认白底。
 * 只作用于 /journal/productDetail，不影响其他页面。
 */
.top-nav--periodical {
  background: #eef7ff;
}

.top-nav__inner {
  padding: 0 160px;
  max-width: 100%;
  height: 60px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

/* 品牌 */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
}
.brand__logo {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: linear-gradient(135deg, #34d399, #0e9f6e);
  background-image: url('../../../public/home/eastview/common/logo.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: inline-block;
  box-shadow: 0 2px 6px rgba(14, 159, 110, 0.3);
}
.brand__logo-img {
  height: 36px;
  width: auto;
  display: inline-block;
  object-fit: contain;
}
.brand__name {
  font-weight: 700;
  font-size: 18px;
  color: #000;
  line-height: 1;
}

/* 导航菜单：覆盖 el-menu 默认样式，选中态使用底部下划线指示 */
.nav-menu {
  border-bottom: none !important;
  background: transparent;
  flex-shrink: 0;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 50px;
}
/* el-submenu 包裹层与 el-menu-item 平级，确保两者容器尺寸一致 */
.nav-menu::v-deep > .el-submenu {
  float: none;
}
/* 一级菜单项 ———— 提高选择器优先级以覆盖 Element UI 的 `.el-menu--horizontal > .el-menu-item` */
.nav-menu::v-deep > .el-menu-item {
  height: 36px;
  line-height: 36px;
  padding: 0 14px;
  margin: 0 4px;
  box-sizing: border-box;
  /* 无边框：选中项仅白色圆角背景，不带任何描边（覆盖 Element UI 默认 active 蓝色下划线） */
  border: none;
  /* 圆角常驻：仅选中项白色圆角矩形背景可见，hover 只改文字颜色 */
  border-radius: 12px;
  color: #000;
  font-size: $themeNavTitleFontsize;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  transition: background 0.2s, color 0.2s;
}
.nav-menu::v-deep > .el-menu-item.is-active {
  /* 选中项：白色圆角矩形背景 + 蓝色文字，无边框 */
  background: #fff;
  border: none;
  border-radius: 12px;
  color: $mainColor;
  font-weight: 700;
}
.nav-menu::v-deep > .el-menu-item:hover {
  /* hover 仅文字变蓝，无背景、无圆角矩形（边框保持透明） */
  background: transparent;
  color: $mainColor;
}
.nav-menu::v-deep > .el-menu-item.is-active:hover {
  /* 选中项 hover 时保持白色圆角矩形边框，不被普通 hover 规则覆盖 */
  background: #fff;
  color: $mainColor;
}

/* el-submenu 标题样式与一级菜单项完全一致（提高选择器优先级） */
.nav-menu::v-deep > .el-submenu .el-submenu__title {
  height: 36px;
  line-height: 36px;
  padding: 0 14px;
  margin: 0 4px;
  box-sizing: border-box;
  /* 无边框：选中项仅白色圆角背景，不带任何描边（覆盖 Element UI 默认 active 蓝色下划线） */
  border: none;
  /* 圆角常驻：仅选中项白色圆角矩形背景可见，hover 只改文字颜色 */
  border-radius: 12px;
  color: #000;
  font-size: $themeNavTitleFontsize;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  transition: background 0.2s, color 0.2s;
}
/* 隐藏 el-submenu 默认下拉箭头，使一级菜单外观完全统一 */
.nav-menu::v-deep .el-submenu__title .el-submenu__icon-arrow,
.nav-menu::v-deep .el-submenu__title .el-icon-arrow-down {
  display: none !important;
}
.nav-menu::v-deep > .el-submenu .el-submenu__title:hover {
  /* hover 仅文字变蓝，无背景、无圆角矩形（边框保持透明） */
  background: transparent;
  color: $mainColor;
}
/* 子菜单高亮时标题着色 + 底部下划线，与普通菜单项 is-active 完全一致 */
.nav-menu::v-deep > .el-submenu.is-active .el-submenu__title {
  /* 选中项：白色圆角矩形背景 + 蓝色文字，无边框 */
  background: #fff;
  border: none;
  border-radius: 12px;
  color: $mainColor;
  font-weight: 700;
}
.nav-menu::v-deep > .el-submenu.is-active .el-submenu__title:hover {
  /* 选中项 hover 时保持白色圆角矩形边框，不被普通 hover 规则覆盖 */
  background: #fff;
  color: $mainColor;
}
/* 弹性间隔 */
.spacer {
  flex: 1;
}

/* 头部操作 */
.actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}
.action {
  cursor: pointer;
  color: #000;
  font-size: 16px;
  display: flex;
  align-items: center;
}
.lang {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  gap: 5px;
}
.cart {
  font-size: 20px;
}
.action.theme {
  padding: 0;
  font-size: 20px;
  color: #333;
}
.nav-btn {
  display: flex;
  align-items: center;
  gap: 10px;
}
.user-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #000;
  font-size: $commonLabelFontsize16;
  cursor: pointer;
  user-select: none;
}
.logout-btn,
.login-btn {
  border: 1px solid #cdd2e1;
  border-radius: 6px;
  color: #000;
  font-size: $commonBtnLabelFontsize;
  padding: 0 16px;
  height: 31px;
  line-height: 29px;
}
.logout-btn {
  color: #333;
}
.logout-btn:hover,
.login-btn:hover {
  border-color: $mainColor;
  color: $mainColor;
}

/* qiushi 非首页：导航栏由透明切换为品牌蓝渐变底（首页仍保持透明沉浸）。
   置于样式末尾，确保同权重下覆盖上方基础导航规则。
   qiushi 无「探索」模块、导航为一级平铺无子导航，因此可放心采用白色导航文字
   （文字色由 el-menu 的 text-color 内联输出，不会波及任何下拉弹层）。 */
.top-nav--gradient {
  background: linear-gradient(180deg, #16a1fe 0%, #0379ff 100%);
}
.top-nav--gradient .brand__name,
.top-nav--gradient .action,
.top-nav--gradient .user-name {
  color: #fff;
}
/* 最右侧登录/退出按钮：渐变导航上保持默认按钮观感（白底 + 常规描边 + 深色文字），
   不复用白字幽灵样式；hover 沿用基础样式转品牌蓝。 */
.top-nav--gradient .login-btn,
.top-nav--gradient .logout-btn {
  background: #fff;
}
/* hover 与选中效果一致：白胶囊底 + 品牌蓝 #0379FF 文字。
   常态白字由 text-color 内联输出，无需在此重复声明。 */
.top-nav--gradient .nav-menu ::v-deep > .el-menu-item:not(.is-disabled):hover,
.top-nav--gradient .nav-menu ::v-deep > .el-menu-item.is-active {
  background: #fff;
  color: #0379ff !important;
  font-weight: 700;
}

v-deep .el-icon-arrow-down {
  font-size: 12px;
}

</style>

<style lang="scss">
/* 子菜单下拉弹层：el-menu 水平菜单的 submenu popup 默认 append-to-body，
   scoped 样式无法命中 body 级弹层，需用全局样式覆盖（总库检索/产品检索下拉子项）。 */
.el-menu--popup .el-menu-item {
  color: #000;
  font-size: $filterTypeLabelFontsize;
  padding: 0 20px;
}
.el-menu--popup .el-menu-item.is-active {
  color: $mainColor;
  font-weight: 700;
  background: #eef0ff;
}
</style>
